/**
 * Button Component
 */

import { UIComponent, UIStyle, MouseEvent } from './UIComponent';
import { getAudioManager } from '@client/audio/AudioManager';

export interface ButtonStyle extends UIStyle {
  hoverBackgroundColor?: string;
  hoverBorderColor?: string;
  hoverTextColor?: string;
  disabledBackgroundColor?: string;
  disabledTextColor?: string;
}

export class Button extends UIComponent {
  protected style: ButtonStyle;
  private label: string;

  constructor(label: string, style: Partial<ButtonStyle> = {}) {
    super(style);
    this.style = { ...this.getDefaultStyle(), ...style } as ButtonStyle;
    this.label = label;
  }

  protected getDefaultStyle(): ButtonStyle {
    return {
      ...super.getDefaultStyle(),
      backgroundColor: '#2a2420',
      borderColor: '#4a3f35',
      borderWidth: 1,
      borderRadius: 5,
      textColor: '#b0a090',
      fontSize: 14,
      fontWeight: 'normal',
      textAlign: 'center',
      padding: { top: 8, right: 16, bottom: 8, left: 16 },
      hoverBackgroundColor: '#3a3430',
      hoverBorderColor: '#d4af37',
      hoverTextColor: '#ffffff',
      disabledBackgroundColor: '#1a1a1a',
      disabledTextColor: '#505050',
    };
  }

  setLabel(label: string): void {
    this.label = label;
  }

  measureSize(ctx: CanvasRenderingContext2D): { width: number; height: number } {
    const padding = this.getPadding();
    const fontSize = this.style.fontSize || 14;
    const fontWeight = this.style.fontWeight === 'bold' ? 'bold ' : '';

    ctx.font = `${fontWeight}${fontSize}px ${this.style.fontFamily || 'Arial'}`;
    const metrics = ctx.measureText(this.label);

    return {
      width: metrics.width + padding.left + padding.right,
      height: fontSize + padding.top + padding.bottom,
    };
  }

  protected renderBackground(ctx: CanvasRenderingContext2D): void {
    const { x, y, width, height } = this.bounds;
    const radius = this.style.borderRadius || 0;

    // Choose colors based on state
    let bgColor = this.style.backgroundColor;
    let borderColor = this.style.borderColor;

    if (!this.enabled) {
      bgColor = this.style.disabledBackgroundColor;
    } else if (this.hovered) {
      bgColor = this.style.hoverBackgroundColor || bgColor;
      borderColor = this.style.hoverBorderColor || borderColor;
    }

    if (bgColor) {
      ctx.fillStyle = bgColor;
      this.drawRoundedRect(ctx, x, y, width, height, radius);
      ctx.fill();
    }

    if (borderColor && this.style.borderWidth) {
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = this.style.borderWidth;
      this.drawRoundedRect(ctx, x, y, width, height, radius);
      ctx.stroke();
    }
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    const fontSize = this.style.fontSize || 14;
    const fontWeight = this.style.fontWeight === 'bold' ? 'bold ' : '';

    // Choose text color based on state
    let textColor = this.style.textColor;
    if (!this.enabled) {
      textColor = this.style.disabledTextColor;
    } else if (this.hovered) {
      textColor = this.style.hoverTextColor || textColor;
    }

    ctx.fillStyle = textColor || '#ffffff';
    ctx.font = `${fontWeight}${fontSize}px ${this.style.fontFamily || 'Arial'}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.fillText(
      this.label,
      this.bounds.x + this.bounds.width / 2,
      this.bounds.y + this.bounds.height / 2
    );
  }
}

/**
 * Icon Button - Button with just an icon or symbol
 */
export class IconButton extends Button {
  constructor(icon: string, style: Partial<ButtonStyle> = {}) {
    super(icon, {
      padding: { top: 6, right: 6, bottom: 6, left: 6 },
      fontSize: 16,
      ...style,
    });
  }
}

/**
 * Tab Button - For tab navigation
 */
export interface TabButtonStyle extends ButtonStyle {
  activeBackgroundColor?: string;
  activeBorderColor?: string;
  activeTextColor?: string;
}

export class TabButton extends UIComponent {
  protected style: TabButtonStyle;
  private label: string;
  private active: boolean = false;
  public onActivate?: () => void;

  constructor(label: string, style: Partial<TabButtonStyle> = {}) {
    super(style);
    this.style = { ...this.getDefaultStyle(), ...style } as TabButtonStyle;
    this.label = label;
  }

  protected getDefaultStyle(): TabButtonStyle {
    return {
      ...super.getDefaultStyle(),
      backgroundColor: '#1a1510',
      borderColor: '#3d3228',
      borderWidth: 1,
      borderRadius: 5,
      textColor: '#808070',
      fontSize: 14,
      fontWeight: 'normal',
      textAlign: 'center',
      padding: { top: 10, right: 20, bottom: 10, left: 20 },
      hoverBackgroundColor: '#2a2420',
      hoverTextColor: '#b0a090',
      activeBackgroundColor: '#3a3530',
      activeBorderColor: '#d4af37',
      activeTextColor: '#d4af37',
    };
  }

  setActive(active: boolean): void {
    this.active = active;
  }

  isActive(): boolean {
    return this.active;
  }

  measureSize(ctx: CanvasRenderingContext2D): { width: number; height: number } {
    const padding = this.getPadding();
    const fontSize = this.style.fontSize || 14;
    const fontWeight = this.active ? 'bold ' : '';

    ctx.font = `${fontWeight}${fontSize}px ${this.style.fontFamily || 'Arial'}`;
    const metrics = ctx.measureText(this.label);

    return {
      width: metrics.width + padding.left + padding.right,
      height: fontSize + padding.top + padding.bottom,
    };
  }

  protected renderBackground(ctx: CanvasRenderingContext2D): void {
    const { x, y, width, height } = this.bounds;
    const radius = this.style.borderRadius || 0;

    let bgColor = this.style.backgroundColor;
    let borderColor = this.style.borderColor;

    if (this.active) {
      bgColor = this.style.activeBackgroundColor;
      borderColor = this.style.activeBorderColor;
    } else if (this.hovered) {
      bgColor = this.style.hoverBackgroundColor;
    }

    if (bgColor) {
      ctx.fillStyle = bgColor;
      this.drawRoundedRect(ctx, x, y, width, height, radius);
      ctx.fill();
    }

    if (borderColor && this.style.borderWidth) {
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = this.active ? 2 : (this.style.borderWidth || 1);
      this.drawRoundedRect(ctx, x, y, width, height, radius);
      ctx.stroke();
    }
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    const fontSize = this.style.fontSize || 14;
    const fontWeight = this.active ? 'bold ' : '';

    let textColor = this.style.textColor;
    if (this.active) {
      textColor = this.style.activeTextColor;
    } else if (this.hovered) {
      textColor = this.style.hoverTextColor;
    }

    ctx.fillStyle = textColor || '#ffffff';
    ctx.font = `${fontWeight}${fontSize}px ${this.style.fontFamily || 'Arial'}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.fillText(
      this.label,
      this.bounds.x + this.bounds.width / 2,
      this.bounds.y + this.bounds.height / 2
    );
  }

  handleMouseEvent(e: MouseEvent): boolean {
    const inside = this.containsPoint(e.x, e.y);

    if (e.type === 'move') {
      this.hovered = inside;
    }

    if (inside && e.type === 'down' && e.button === 'left' && !this.active) {
      if (this.onActivate) {
        getAudioManager().play('ui_confirm');
        this.onActivate();
      }
      return true;
    }

    return false;
  }
}
